import { Auth } from '@aws-amplify/auth';
import { QueryClient } from '@tanstack/react-query';

import { LocalizedException } from '@/modules/common/exceptions/localized-exception';
import { OpsiumUser } from '@/modules/common/types/opsium-user';
import { NOTIFICATION_PROMPT_OPTED_OUT_STORAGE_KEY } from '@/modules/notifications/constants/notification-storage-keys';
import notificationService from '@/modules/notifications/firebase-notification.service';
import { clearLocalStorage } from '@/utils/localStorage';

export interface ActivateAccountInput {
  email: string;
  tempPassword: string;
  newPassword: string;
}

export async function activateAccount({
  email,
  tempPassword,
  newPassword,
}: ActivateAccountInput): Promise<OpsiumUser> {
  try {
    // If already signed in, sign out first
    await Auth.signOut();
  } catch {
    // Ignore
  }

  const user = await Auth.signIn(email, tempPassword);
  await Auth.completeNewPassword(user, newPassword);
  return {
    email: email,
    name: '',
    externalRate: undefined,
    internalRate: undefined,
  };
}

export async function checkIsTempPasswordExpired(
  email: string,
  tempPassword: string
): Promise<boolean> {
  try {
    // Try to sign in with the temp password. If it works, the link is valid.
    await Auth.signIn(email, tempPassword);
    await Auth.signOut();
    return false;
  } catch (error: any) {
    if (
      error instanceof Error &&
      (error.name === 'NotAuthorizedException' || error.name === 'UserNotFoundException')
    ) {
      return true;
    }
    throw error;
  }
}

export enum AuthErrorCode {
  NotAuthorized = 'NotAuthorized',
  UserNotConfirmed = 'UserNotConfirmed',
  UserNotFound = 'UserNotFound',
  Unknown = 'Unknown',
}

export async function signIn(email: string, password: string): Promise<OpsiumUser> {
  try {
    const user = await Auth.signIn(email, password);
    if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
      // noinspection ExceptionCaughtLocallyJS
      throw new LocalizedException('User must set a new password', AuthErrorCode.UserNotConfirmed);
    }
    return {
      email: email,
      name: '',
      externalRate: undefined,
      internalRate: undefined,
    };
  } catch (error: any) {
    if (error instanceof LocalizedException) {
      throw error;
    }
    let code: AuthErrorCode;
    switch (error.code) {
      case 'UserNotConfirmedException':
        code = AuthErrorCode.UserNotConfirmed;
        break;
      case 'NotAuthorizedException':
        code = AuthErrorCode.NotAuthorized;
        break;
      case 'UserNotFoundException':
        code = AuthErrorCode.UserNotFound;
        break;
      default:
        code = AuthErrorCode.Unknown;
    }
    throw new LocalizedException(error.message, code);
  }
}

export async function sendPasswordResetEmail(email: string): Promise<void> {
  await Auth.forgotPassword(email);
}

export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string
): Promise<OpsiumUser> {
  await Auth.forgotPasswordSubmit(email, code, newPassword);
  return {
    email,
    name: '',
    externalRate: undefined,
    internalRate: undefined,
  };
}

export async function signOut(queryClient?: QueryClient): Promise<void> {
  // Unregister device from push notifications before logout
  await notificationService.unregisterDevice();

  // Clear all TanStack Query cache and cancel ongoing queries first
  if (queryClient) {
    await queryClient.cancelQueries();
    queryClient.clear();
  }

  clearLocalStorage({ keysToKeep: [NOTIFICATION_PROMPT_OPTED_OUT_STORAGE_KEY] });

  await Auth.signOut();
}

export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  const user = await Auth.currentAuthenticatedUser();
  await Auth.changePassword(user, oldPassword, newPassword);
}
